import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@selfai-platform/shared';
import {
  Agent,
  AgentExecutionRequest,
  AgentExecutionResponse,
  AgentType,
  AgentValidationResult,
} from '../models';
import { LLMOrchestratorService } from './llm-orchestrator.service';
import { RunAppRequest } from '../types/service-interfaces';
import { AgentMappingsService } from './agent-mappings.service';

@Injectable({
  providedIn: 'root',
})
export class AgentService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly availableAgentsSubject = new BehaviorSubject<Agent[]>([]);
  private readonly selectedAgentSubject = new BehaviorSubject<Agent | null>(null);
  private readonly agentHealthSubject = new BehaviorSubject<boolean>(false);

  public availableAgents$ = this.availableAgentsSubject.asObservable();
  public selectedAgent$ = this.selectedAgentSubject.asObservable();
  public agentHealth$ = this.agentHealthSubject.asObservable();

  constructor(
    private llmOrchestratorService: LLMOrchestratorService,
    private translateService: TranslateService,
    private agentMappingsService: AgentMappingsService,
    private readonly alertService: AlertService
  ) {
    this.initializeAgents();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeAgents(): void {
    this.checkAgentHealth().pipe(
      switchMap((isHealthy) => {
        if (isHealthy) {
          return this.loadAvailableAgents();
        } else {
          console.warn('Agent backend is unhealthy, skipping agent loading');
          return of([]);
        }
      })
    ).subscribe({
      next: (agents) => {
        if (agents.length > 0) {
          console.log(`Loaded ${agents.length} available agents`);
        }
      },
      error: (error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-initialize-agents') + ': ' + (error.message || error));
      }
    });
  }

  public checkAgentHealth(): Observable<boolean> {
    return this.llmOrchestratorService.checkHealth().pipe(
      map((health) => health.status === 'healthy'),
      tap((isHealthy) => {
        this.agentHealthSubject.next(isHealthy);
        if (!isHealthy) {
          const agents = this.availableAgentsSubject.value.map(agent => ({
            ...agent,
            isAvailable: false,
          }));
          this.availableAgentsSubject.next(agents);
        }
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.agent-health-check-error') + ': ' + (error.message || error));
        this.agentHealthSubject.next(false);
        return of(false);
      })
    );
  }

  public loadAvailableAgents(): Observable<Agent[]> {
    return this.llmOrchestratorService.getApps().pipe(
      map((appsResponse) => {
        const agents: Agent[] = [];

        if (appsResponse && typeof appsResponse === 'object') {
          if (appsResponse.pipeline) {
            Object.entries(appsResponse.pipeline).forEach(([appId, appData]) => {
              const agentType = this.mapAppIdToAgentType(appId);
              if (agentType) {
                const agent: Agent = {
                  id: appId,
                  type: agentType,
                  name: appData.name || this.getAgentDisplayName(appId),
                  description: appData.desc || this.getAgentDescription(appId),
                  app_class: appData.app_class,
                  category: 'pipeline',
                  isAvailable: true,
                  requiresCube: false,
                };
                agents.push(agent);
              }
            });
          }

          if (appsResponse.common) {
            Object.entries(appsResponse.common).forEach(([appId, appData]) => {
              const agentType = this.mapAppIdToAgentType(appId);
              if (agentType) {
                const agent: Agent = {
                  id: appId,
                  type: agentType,
                  name: appData.name || this.getAgentDisplayName(appId),
                  description: appData.desc || this.getAgentDescription(appId),
                  app_class: appData.app_class,
                  category: 'common',
                  isAvailable: true,
                  requiresCube: false,
                };
                agents.push(agent);
              }
            });
          }
        }

        this.availableAgentsSubject.next(agents);
        return agents;
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.failed-to-load-available-agents') + ': ' + (error.message || error));
        const emptyAgents: Agent[] = [];
        this.availableAgentsSubject.next(emptyAgents);
        return of(emptyAgents);
      })
    );
  }

  private mapAppIdToAgentType(appId: string): AgentType | null {
    return appId as AgentType;
  }

  private getAgentDisplayName(key: string): string {
    const agentType = key as AgentType;
    return this.agentMappingsService.getAgentDisplayName(agentType);
  }

  private getAgentDescription(key: string): string {
    const translationKey = `chat.agents.${key.replace('_', '-')}.description`;
    const translated = this.translateService.instant(translationKey);

    if (translated !== translationKey) {
      return translated;
    }

    return this.translateService.instant('chat.agents.default.description', { agentKey: key });
  }

  public selectAgent(agent: Agent): Observable<void> {
    const validationResult = this.validateAgentSelection(agent);
    if (!validationResult.isValid) {
      const errorMessage = this.translateService.instant('chat.agents.errors.validation-failed', {
        errors: validationResult.errors.join(', ')
      });
      return throwError(() => new Error(errorMessage));
    }

    this.selectedAgentSubject.next(agent);
    return of(void 0);
  }

  public validateAgentSelection(agent: Agent): AgentValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!agent.isAvailable) {
      errors.push(this.translateService.instant('chat.agents.errors.not-available'));
    }

    if (!this.agentHealthSubject.value) {
      errors.push(this.translateService.instant('chat.agents.errors.backend-unhealthy'));
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      requiredFields: [],
    };
  }

  public executeAgent(request: AgentExecutionRequest): Observable<AgentExecutionResponse> {
    const agent = this.availableAgentsSubject.value.find(a => a.type === request.agentType);
    if (!agent) {
      const errorMessage = this.translateService.instant('chat.agents.errors.not-found', {
        agentType: request.agentType
      });
      return throwError(() => new Error(errorMessage));
    }

    const agentValidation = this.validateAgentSelection(agent);
    if (!agentValidation.isValid) {
      const errorMessage = this.translateService.instant('chat.agents.errors.validation-failed', {
        errors: agentValidation.errors.join(', ')
      });
      return throwError(() => new Error(errorMessage));
    }

    const runAppRequest = this.createRunAppRequest(request, agent);

    return this.llmOrchestratorService.runApp(runAppRequest).pipe(
      map((response): AgentExecutionResponse => {
        const primary = response.output?.primary;
        const content = primary?.response || primary?.explanation || primary?.reply || '';
        
        return {
          success: response.metadata?.status === 'success',
          content: content,
          threadId: response.thread_id,
          executionId: response.execution_id,
          result: response,
          metadata: {
            executionTime: response.metadata?.execution_time_ms || Date.now(),
            plan: response.debug?.steps?.join(', ') || '',
            code: primary?.code,
            explanation: primary?.explanation,
            reply: primary?.reply,
            errors: response.errors,
            warnings: response.output?.warnings,
            suggestions: response.suggestions,
            debug: response.debug,
            ...response.metadata,
          },
        };
      }),
      catchError((error) => {
        this.alertService.error(this.translateService.instant('chat.errors.agent-execution-failed-simple') + ': ' + (error.message || error));
        return of({
          success: false,
          content: '',
          error: {
            message: error.message || this.translateService.instant('chat.agents.errors.execution-failed'),
            code: error.status || 'EXECUTION_ERROR',
            details: error,
          },
        });
      })
    );
  }

  private createRunAppRequest(request: AgentExecutionRequest, agent: Agent): RunAppRequest {
    return {
      app_id: agent.id,
      input_params: {
        user_query: request.query,
        schema: request.context.schema || [],
        user_id: request.context.userId || 'default-user',
        message: request.query,
        workflowId: request.context.workflowId,
        cubeId: request.context.cubeId,
        cubeName: request.context.cubeName,
        inputNodeIds: request.context.inputNodeIds,
        serializedData: request.context.serializedData,
      },
      thread_id: request.threadId,
      save_history: request.saveHistory ?? true,
      generate_title: request.generateTitle ?? false,
    };
  }

  private extractContentFromResponse(result: any): string {
    if (typeof result === 'string') {
      return result;
    }

    if (result && typeof result === 'object') {
      if (result.reply) {
        return result.reply;
      }
      if (result.content) {
        return result.content;
      }
      if (result.message) {
        return result.message;
      }
      return JSON.stringify(result);
    }

    return String(result || '');
  }

  public getPipelineAgents(): Observable<Agent[]> {
    return this.availableAgents$.pipe(
      map(agents => agents.filter(agent => agent.category === 'pipeline'))
    );
  }

  public getCommonAgents(): Observable<Agent[]> {
    return this.availableAgents$.pipe(
      map(agents => agents.filter(agent => agent.category === 'common'))
    );
  }

  public refreshAgents(): Observable<Agent[]> {
    return this.checkAgentHealth().pipe(
      switchMap(() => this.loadAvailableAgents())
    );
  }
}
